import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    Alert,
    Button,
    ColumnLayout,
    Header
} from '@cloudscape-design/components'
import FormInput from '../Input'
import { useForm } from 'react-hook-form'
import FormSelect from '../Select'
import { useEditTagsManagementFormStore } from '../../../stores/tags/editTagsManagementFormStore'
import useSingleTags from '../../../hooks/query/useSingleTags'
import usePlatformAliases from '../../../hooks/query/usePlatformAlases'
import usePlatformOptions from '../../../hooks/query/usePlatformOptions'
import LoadingSpinner from '../../LoadingSpinner'
 
import CONSTANT from '../../../constants'
import {
	COST_CENTER_PATTERN,
	WBS_CODE_PATTERN
} from '../../../helper/fundingCodeValidation'
 
import { UpdateTags } from '../../../types/tags'
import useUpdateTags from '../../../hooks/mutate/useUpdateTags'
import ApproverOverview from '../ApproverOverview'
import { useOktaAuth } from '@okta/okta-react'
import PORTFOLIO_LIST from '../../../portfolio'
import useSingleGcpProject from '../../../hooks/query/useSingleGcpProject'
import { getProjectLevelApprovals } from '../../../helper/applicationTagApproval'
import type { PlatformOwnerResource } from '../../../helper/platformOwnerPermissions'
 
const { COST_TYPE_LIST, DATA_CLASSIFICATION_LIST } = CONSTANT
 
interface EditGenericTagsProps {
    tagsId: string
    layout_col?: number
    page_context?: string
    isPvm?: boolean
    platformOwnerResource?: PlatformOwnerResource
    platformOwnerEmail?: string
}
 
const EditGenericTags: FC<EditGenericTagsProps> = ({
    tagsId,
    layout_col = 2,
    isPvm = true,
    platformOwnerResource,
    platformOwnerEmail = ''
}) => {
    const {
        data: platformOptions = [],
        isLoading: isLoadingPlatformOptions,
        isError: isPlatformOptionsError
    } = usePlatformOptions(isPvm)

    const { data: platformAliases = [] } = usePlatformAliases();
    const { authState } = useOktaAuth()
    const thisUserEmail = authState?.idToken?.claims.email as string
 
    const { data: originalTags, isLoading: isLoadingTags } = useSingleTags(tagsId)

    const { id: gcpProjectID } = useParams() as { id: string }
    const { data: thisgcpProject } = useSingleGcpProject(gcpProjectID, true)
 
    const {
        formValues,
        actions: { setFormValues, setFormValidity, setOriginalRequestFormValues }
    } = useEditTagsManagementFormStore()
 
    const { mutate: updateTagsById, isLoading: isLoadingUpdateTags } =
        useUpdateTags(tagsId)
 
    const {
        control,
        reset,
        setValue,
        formState: { isValid, dirtyFields }
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: formValues.details,
        shouldUseNativeValidation: true
    })
 
    useEffect(() => {
        if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'opex') {
            setValue('WBSCode', '')
            setFormValues('WBSCode', '')
        } else if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'capex') {
            setValue('costCenter', '')
            setFormValues('costCenter', '')
        }
    }, [formValues.fundingSourceType, setValue, setFormValues])
 
    useEffect(() => {
        setFormValidity(isValid)
    }, [isValid, setFormValidity])

    useEffect(() => {
		const groupValue = formValues.group?.value ?? '';
		let projectType = '';
		if (thisgcpProject?.type === 'Develop') {
			projectType = 'dev';
		} else if (thisgcpProject?.type === 'Staging') {
			projectType = 'stg';
		} else if (thisgcpProject?.type === 'Production') {
			projectType = 'prod';
		} else if (thisgcpProject?.type === 'Sandbox') {
			projectType = 'sbx';
        }
		const projectNameValue = `glo-${groupValue}-${formValues.systemAlias}-project-${projectType}`;
		setValue('projectName', projectNameValue);
		setFormValues('projectName', projectNameValue);
	}, [formValues.group?.value, formValues.systemAlias, thisgcpProject?.type, setValue, setFormValues]);
 
    const [notification, setNotification] = useState<any>({
        type: '',
        statusIconAriaLabel: '',
        msg: ''
    })
    const [showTagsNotification, setShowTagsNotification] = useState(false)
 
    useEffect(() => {
        if (typeof originalTags === 'object' && originalTags !== null) {
            setOriginalRequestFormValues({
                formValues: originalTags.details,
                isFormValid: false
            })
            reset(originalTags.details)
        }
        // eslint-disable-next-line
    }, [originalTags])
 
    if (!originalTags || isLoadingTags) return <LoadingSpinner />

    const saveUpdatedTags = () => {
        console.log('save Updated Tags')
        const updateTagRequest: UpdateTags = {
            tagsID: tagsId,
            requestorID: thisUserEmail,
            action: 'UPDATE_TAGS',
            status: 'PENDING_APPROVAL',
            // UPDATE_TAGS overwrites details wholesale, and this form only holds tag
            // fields - carry the application registry through so editing project tags
            // does not wipe the applications inheriting this record.
            details: {
                ...formValues,
                ...(originalTags.details?.applications
                    ? { applications: originalTags.details.applications }
                    : {})
            }
        }
        updateTagsById(updateTagRequest, {
            onSuccess: () => {
                setNotification({
                    type: 'success',
                    statusIconAriaLabel: 'Success',
                    msg: 'Tags Status Updated Successfully'
                })
                setShowTagsNotification(true)
                setTimeout(() => {
                    setShowTagsNotification(false)
                }, 3000)
            },
            onError: () => {
                setNotification({
                    type: 'error',
                    statusIconAriaLabel: 'Error',
                    msg: 'Error saving Tags, Please try again!'
                })
                setShowTagsNotification(true)
                setTimeout(() => {
                    setShowTagsNotification(false)
                }, 3000)
            }
        })
    }
 
    return (
        <>
            <ColumnLayout columns={layout_col} variant="default">
            {isPvm && (
                <FormSelect
                    name="platformName"
                    label="Platform Name"
                    control={control}
                    rules={{
                        required: 'Please Select Option'
                    }}
                    setFormValues={setFormValues}
                    options={platformOptions}
                    placeholder="Select Platform "
                    disabled={isLoadingPlatformOptions || isPlatformOptionsError}
                    statusType={isLoadingPlatformOptions ? 'loading' : undefined}
                />
            )}
			<FormInput
				name="platformOwner"
				label="Platform Owner"
				type="text"
				placeholder="Enter Platform Owner"
				control={control}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="systemAlias"
				label="System Alias"
				type="text"
				placeholder="Example: hipms, namax"
				control={control}
				rules={{
					required: 'System alias should be 5 letters, Example: hipms',
					pattern: {
						value: /^[A-Za-z][A-Za-z0-9-]{4,}$/g,
						message: 'System alias should be 5 letters, Example: hipms'
					},
					minLength: {
						value: 5,
						message: 'System alias should be 5 letters, Example: hipms'
					},
					maxLength: {
						value: 5,
						message: 'System alias should be 5 letters, Example: namax'
					},
                    validate: {
                    // 1. Local check against pre-fetched list
                        isUnique: (value: string) => {
                            if (!value) return true;    
                            // Normalize aliases and current value to lower case for comparison
                            const isDuplicate = platformAliases.some(
                            (alias) => alias.toLowerCase() === value.trim().toLowerCase()
                            );
                            return !isDuplicate || 'System alias already exists. Please choose a different alias.';
                        }    
                    }
				}}
				setFormValues={setFormValues}
			/>
			<FormSelect
				label="Group"
				name="group"
				control={control}
				rules={{
					required: 'Please Select Option'
				}}
				setFormValues={setFormValues}
				options={PORTFOLIO_LIST}
				placeholder="Select Group"
			/>
            <FormSelect
                label="Data Classification"
                name="dataClassification"
                control={control}
                rules={{
                    required: 'Please Select Option'
                }}
                setFormValues={setFormValues}
                options={DATA_CLASSIFICATION_LIST}
                placeholder="Select Data Classification"
            />
            {isPvm && (
                <>
                        <FormInput
                            name="projectName"
                            label="Project Name"
                            type="text"
                            control={control}
                            setFormValues={setFormValues}
                            placeholder=""
                            disabled={true}
                        />
                        <br />
                </>
            )}
            {isPvm && (<br/>)}
			<FormSelect
				label="Funding Source"
				name="fundingSourceType"
				control={control}
				rules={{
					required: 'Please Select Option'
				}}
				setFormValues={setFormValues}
				options={COST_TYPE_LIST}
				placeholder="Select Funding Source Type"
			/>
                {isPvm && (<br />)}
			<FormInput
				name="WBSCode"
				label="WBS Code"
				type="text"
				control={control}
				rules={{
					pattern: {
						value: WBS_CODE_PATTERN,
						message: 'Please Enter in all CAPS with at least 3 parts, separated by dashes. Ex. PSD-OMN-21, C-D-PR02-JE67CT-89-HW-01-GL-BE'
					},
					minLength: {
						value: 3,
						message: 'Minimum length should be 3'
					}
				}}
				setFormValues={setFormValues}
				placeholder="Example: ISG-PROJECTA-23, EDO-PROJECTC-24-SW-01, A-B-PR01-JE23CT-45, E-F-PR03-JE45CT-91-BE-GL"
				disabled={
					formValues?.fundingSourceType &&
					formValues.fundingSourceType.value === 'opex'
				}
			/>
			<FormInput
				name="costCenter"
				label="Cost Center"
				type="text"
				control={control}
				rules={{
					pattern: {
						value: COST_CENTER_PATTERN,
						message: 'Please Enter all in CAPS, Ex. OP20-G,ISG24-G,PSD-OMN-21'
					},
					minLength: {
						value: 3,
						message: 'Minimum length should be 3'
					}
				}}
				setFormValues={setFormValues}
				placeholder="Example: ISG17-A, OP20-G, MP-CIV-20, HTT7-B,ISG01,PSD-TECCF/19"
				disabled={
					formValues?.fundingSourceType &&
					formValues.fundingSourceType.value === 'capex'
				}
			/>
            </ColumnLayout>
            <br />
            {Object.keys(dirtyFields).length > 0 && (
                <>
                    <Header
                        variant="h3"
                        description="Existing Tags has been updated, Would you like to save updated Tags"
                    ></Header>
                    <Button
                        variant="primary"
                        onClick={saveUpdatedTags}
                        loading={isLoadingUpdateTags}
                    >
                        Save Tags{' '}
                    </Button>
                    <br />
                    {showTagsNotification && (
                        <>
                            <br />
                            <Alert
                                statusIconAriaLabel={notification.statusIconAriaLabel}
                                type={notification.type}
                            >
                                {notification.msg}
                            </Alert>
                        </>
                    )}
                </>
            )}
 
            <br />
 
            <ApproverOverview
                approvalStatus={getProjectLevelApprovals(
                    originalTags['approvalStatus']
                )}
                tagsID={tagsId}
                platformOwnerResource={platformOwnerResource ?? {
                    type: 'gcpProjects',
                    id: gcpProjectID
                }}
                tagDetails={{
                    ...originalTags['details'],
                    platformOwnerEmail:
                        platformOwnerEmail ||
                        originalTags['details']?.platformOwnerEmail ||
                        thisgcpProject?.details?.[thisgcpProject?.type]?.platformOwnerEmail ||
                        thisgcpProject?.details?.platformOwnerEmail
                }}
                description="Tick checkBox to Provide approval"
                //isDisabled={page_context == 'PROJECT' ? true : false}
            />
        </>
    )
}

export default EditGenericTags